import { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'

const DEFAULT_VOICE_CONFIG = {
  tone: 'profesional',
  style: 'informativo',
  formality: 'formal',
}

const DEFAULT_FORM_DATA: PersonaFormData = {
  name: '',
  role: '',
  description: '',
  avatar_url: '',
  expertise_areas: [],
  platforms: [],
  signature_phrases: [],
  voice_config: { ...DEFAULT_VOICE_CONFIG },
}

interface PersonaFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: PersonaFormData) => Promise<void>
  initialData?: PersonaFormData
  isLoading?: boolean
}

export interface PersonaFormData {
  name: string
  role: string
  description?: string
  avatar_url?: string
  expertise_areas?: string[]
  platforms?: string[]
  signature_phrases?: string[]
  voice_config?: {
    tone?: string
    style?: string
    formality?: string
  }
}

export function PersonaForm({ isOpen, onClose, onSubmit, initialData, isLoading }: PersonaFormProps) {
  const [formData, setFormData] = useState<PersonaFormData>(DEFAULT_FORM_DATA)
  const [expertiseInput, setExpertiseInput] = useState('')
  const [platformInput, setPlatformInput] = useState('')
  const [phraseInput, setPhraseInput] = useState('')

  useEffect(() => {
    if (!isOpen) return
    const vc = initialData?.voice_config
    if (initialData) {
      setFormData({
        name: initialData.name ?? '',
        role: initialData.role ?? '',
        description: initialData.description ?? '',
        avatar_url: initialData.avatar_url ?? '',
        expertise_areas: initialData.expertise_areas ?? [],
        platforms: initialData.platforms ?? [],
        signature_phrases: initialData.signature_phrases ?? [],
        voice_config: vc
          ? {
              tone: vc.tone ?? DEFAULT_VOICE_CONFIG.tone,
              style: vc.style ?? DEFAULT_VOICE_CONFIG.style,
              formality: vc.formality ?? DEFAULT_VOICE_CONFIG.formality,
            }
          : { ...DEFAULT_VOICE_CONFIG },
      })
    } else {
      setFormData({ ...DEFAULT_FORM_DATA })
    }
    setExpertiseInput('')
    setPlatformInput('')
    setPhraseInput('')
  }, [isOpen, initialData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
    onClose()
  }

  const addItem = (field: 'expertise_areas' | 'platforms' | 'signature_phrases', value: string) => {
    if (!value.trim()) return
    
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), value.trim()],
    }))
  }

  const removeItem = (field: 'expertise_areas' | 'platforms' | 'signature_phrases', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index),
    }))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={initialData ? 'Editar Persona' : 'Nueva Persona'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Nombre *</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">Rol *</label>
            <select
              className="input"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            >
              <option value="">Seleccionar...</option>
              <option value="CEO">CEO</option>
              <option value="CTO">CTO</option>
              <option value="Marketing">Marketing</option>
              <option value="Brand">Brand</option>
              <option value="Guest">Guest</option>
            </select>
          </div>
        </div>

        <div>
          <label className="label">Descripción</label>
          <textarea
            className="input"
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div>
          <label className="label">URL del avatar</label>
          <input
            type="url"
            className="input"
            placeholder="https://..."
            value={formData.avatar_url ?? ''}
            onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="label">Tono</label>
            <select
              className="input"
              value={formData.voice_config?.tone}
              onChange={(e) => setFormData({ 
                ...formData, 
                voice_config: { ...formData.voice_config, tone: e.target.value } 
              })}
            >
              <option value="profesional">Profesional</option>
              <option value="amigable">Amigable</option>
              <option value="tecnico">Técnico</option>
              <option value="inspirador">Inspirador</option>
            </select>
          </div>

          <div>
            <label className="label">Estilo</label>
            <select
              className="input"
              value={formData.voice_config?.style}
              onChange={(e) => setFormData({ 
                ...formData, 
                voice_config: { ...formData.voice_config, style: e.target.value } 
              })}
            >
              <option value="informativo">Informativo</option>
              <option value="narrativo">Narrativo</option>
              <option value="analitico">Analítico</option>
              <option value="conversacional">Conversacional</option>
            </select>
          </div>

          <div>
            <label className="label">Formalidad</label>
            <select
              className="input"
              value={formData.voice_config?.formality}
              onChange={(e) => setFormData({ 
                ...formData, 
                voice_config: { ...formData.voice_config, formality: e.target.value } 
              })}
            >
              <option value="formal">Formal</option>
              <option value="semiformal">Semi-formal</option>
              <option value="informal">Informal</option>
            </select>
          </div>
        </div>

        <div>
          <label className="label">Áreas de expertise</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Agregar área de expertise..."
              value={expertiseInput}
              onChange={(e) => setExpertiseInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addItem('expertise_areas', expertiseInput)
                  setExpertiseInput('')
                }
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                addItem('expertise_areas', expertiseInput)
                setExpertiseInput('')
              }}
            >
              Agregar
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.expertise_areas?.map((area, index) => (
              <span key={index} className="badge badge-info flex items-center gap-1">
                {area}
                <button
                  type="button"
                  onClick={() => removeItem('expertise_areas', index)}
                  className="hover:text-red-600"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className="label">Plataformas</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Agregar plataforma..."
              value={platformInput}
              onChange={(e) => setPlatformInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addItem('platforms', platformInput)
                  setPlatformInput('')
                }
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                addItem('platforms', platformInput)
                setPlatformInput('')
              }}
            >
              Agregar
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.platforms?.map((platform, index) => (
              <span key={index} className="badge badge-success flex items-center gap-1">
                {platform}
                <button
                  type="button"
                  onClick={() => removeItem('platforms', index)}
                  className="hover:text-red-600"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className="label">Frases características</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Agregar frase..."
              value={phraseInput}
              onChange={(e) => setPhraseInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addItem('signature_phrases', phraseInput)
                  setPhraseInput('')
                }
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                addItem('signature_phrases', phraseInput)
                setPhraseInput('')
              }}
            >
              Agregar
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.signature_phrases?.map((phrase, index) => (
              <span key={index} className="badge text-slate-600 bg-slate-100 flex items-center gap-1">
                {phrase}
                <button
                  type="button"
                  onClick={() => removeItem('signature_phrases', index)}
                  className="hover:text-red-600"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Guardando...' : (initialData ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
